import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";

const FormStatusAlert = ({
  status,
  open,
  handleClose,
  formId,
  fetchData,
  beforeStatusChange,
  requireSyncPassword = false,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [syncPassword, setSyncPassword] = useState("");

  useEffect(() => {
    if (!open) {
      setSyncPassword("");
      setError("");
    }
  }, [open]);

  const handleCloseDialog = () => {
    handleClose();
    setError("");
    setSyncPassword("");
  };
  const handleConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      if (status === "active" && beforeStatusChange) {
        const saveSucceeded = await beforeStatusChange();

        if (saveSucceeded === false) {
          throw new Error("Failed to save form changes before finalizing");
        }
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/change-status`,
        {
          formId,
          currentSession,
          status,
          ...(requireSyncPassword ? { syncPassword } : {}),
        },
        {
          withCredentials: true,
        },
      );

      if (response?.data?.success) {
        await fetchData?.(response.data.data);
        handleCloseDialog();
      }
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          error?.message ||
          `An error occurred while changing form status`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogContent>
        <Typography variant="body1">
          {status === "inactive"
            ? "Are you sure you want to inactivate this form?"
            : "Are you sure you want to activate this form?"}
        </Typography>
        {requireSyncPassword && status === "inactive" && (
          <>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This form is synchronized with production. Enter the document
              synchronization password to continue with the production and
              development usage checks.
            </Alert>
            <TextField
              autoFocus
              fullWidth
              type="password"
              label="Synchronization Password"
              value={syncPassword}
              onChange={(event) => setSyncPassword(event.target.value)}
              disabled={loading}
              autoComplete="new-password"
              sx={{ mt: 2 }}
            />
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCloseDialog}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={requireSyncPassword && !syncPassword.trim()}
        >
          {status === "inactive" ? "Inactivate Form" : "Activate"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FormStatusAlert;
